<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm :schema="schema" :state="state" :validate-on="['submit']" class="h-full" @submit="onSubmit">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            {{ titles[mode] }}
                        </h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                    </div>
                </template>
                <div class="space-y-6">
                    <p v-if="destructive" class="text-sm text-gray-600">
                        {{ mode === 'expired' ? 'The quantity below will be removed from stock as expired.' : 'The quantity below will be removed from this post and returned to HDF.' }}
                    </p>

                    <div v-if="lot" class="grid grid-cols-2 gap-4">
                        <Field label="Item">
                            {{ lot.item?.name }}<span v-if="lot.item?.specification" class="text-gray-500"> · {{ lot.item.specification }}</span>
                        </Field>
                        <Field label="Lot">
                            <span v-if="lot.lot_number" class="font-mono">{{ lot.lot_number }}</span>
                        </Field>
                        <Field label="Storage">
                            <span v-if="lot.storage?.name">{{ lot.storage.name }}</span>
                        </Field>
                        <Field label="On Hand">
                            {{ formatQty(lot.qty_on_hand) }} <span class="text-sm text-gray-500">{{ lot.item?.unit }}</span>
                        </Field>
                        <Field label="Expires">
                            <span v-if="lot.expires_on">{{ dayjs(lot.expires_on).format('MMM D, YYYY') }}</span>
                        </Field>
                    </div>

                    <template v-if="mode === 'restock' && !lot">
                        <UFormGroup name="item_id" label="Item">
                            <USelectMenu v-model="state.item_id" :options="itemOptions" value-attribute="id" option-attribute="label"
                                searchable searchable-placeholder="Search..." size="xl" placeholder="Select item..." />
                        </UFormGroup>
                        <UFormGroup name="storage_id" label="Storage">
                            <USelectMenu v-model="state.storage_id" :options="post.storages ?? []" value-attribute="id" option-attribute="name"
                                size="xl" placeholder="Select storage..." />
                        </UFormGroup>
                        <UFormGroup name="lot_number" label="Lot Number">
                            <UInput v-model="state.lot_number" size="xl" />
                        </UFormGroup>
                        <UFormGroup name="expires_on" label="Expiry Date">
                            <UInput v-model="state.expires_on" type="date" size="xl" />
                        </UFormGroup>
                    </template>

                    <UFormGroup v-if="mode === 'transfer'" name="dest_storage_id" label="Destination">
                        <USelectMenu v-model="state.dest_storage_id" :options="destinationOptions" value-attribute="id" option-attribute="label"
                            size="xl" placeholder="Select destination..." />
                    </UFormGroup>

                    <UFormGroup name="qty" :label="mode === 'adjust' ? 'Counted Quantity' : 'Quantity'"
                        :help="mode === 'adjust' ? 'Enter what is physically there. The difference is recorded.' : undefined">
                        <UInput v-model="state.qty" type="number" inputmode="decimal" step="any" size="xl" :input-class="unit ? 'pe-20' : undefined">
                            <template v-if="unit" #trailing>
                                <span class="text-gray-400 text-sm">{{ unit }}</span>
                            </template>
                        </UInput>
                    </UFormGroup>

                    <UFormGroup name="note" label="Note">
                        <UTextarea v-model="state.note" :rows="2" size="xl" placeholder="Optional note..." />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>
                        <UButton type="submit" size="lg" :loading="saving" :color="destructive ? 'red' : 'black'">
                            {{ submitLabels[mode] }}
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { z } from 'zod'
import type { FieldPost, InventoryItem, StockActionMode, StockLot } from '~/types/fieldPost'
import { formatQty } from '~/utils/fieldPosts'

const props = defineProps<{
    open: boolean
    post: FieldPost
    mode: StockActionMode
    lot?: StockLot
    items: InventoryItem[]
    destinations: { id: string; label: string }[]
}>()

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const client = useSupabaseClient()
const toast = useToast()

const titles: Record<StockActionMode, string> = {
    restock: 'Restock',
    adjust: 'Adjust Count',
    transfer: 'Transfer Stock',
    return_hdf: 'Return to HDF',
    expired: 'Mark Expired',
}

const submitLabels: Record<StockActionMode, string> = {
    restock: 'Restock',
    adjust: 'Save',
    transfer: 'Transfer',
    return_hdf: 'Return to HDF',
    expired: 'Mark Expired',
}

const destructive = computed(() => props.mode === 'return_hdf' || props.mode === 'expired')

const itemOptions = computed(() => props.items.map(i => ({ id: i.id, label: [i.name, i.specification, i.unit].filter(Boolean).join(' · ') })))
const destinationOptions = computed(() => props.destinations.filter(d => d.id !== props.lot?.storage_id))

const saving = ref(false)
const state = reactive({
    item_id: null as string | null,
    storage_id: null as string | null,
    lot_number: '',
    expires_on: '',
    dest_storage_id: null as string | null,
    qty: '' as string | number,
    note: '',
})

const unit = computed(() => props.lot?.item?.unit ?? props.items.find(i => i.id === state.item_id)?.unit)

const required = z.string({ required_error: 'Required', invalid_type_error: 'Required' }).min(1, 'Required')

const schema = computed(() => {
    const newLot = props.mode === 'restock' && !props.lot
    return z.object({
        item_id: newLot ? required : z.any(),
        storage_id: newLot ? required : z.any(),
        dest_storage_id: props.mode === 'transfer' ? required : z.any(),
        qty: z.union([z.string(), z.number()]).refine(
            v => v !== '' && Number.isFinite(Number(v)) && (props.mode === 'adjust' || Number(v) > 0),
            'Enter a valid quantity',
        ),
    })
})

watch(() => props.open, (open) => {
    if (!open) return
    state.item_id = null
    state.storage_id = props.post.storages?.[0]?.id ?? null
    state.lot_number = ''
    state.expires_on = ''
    state.dest_storage_id = null
    state.qty = props.mode === 'adjust' && props.lot ? Number(props.lot.qty_on_hand) : ''
    state.note = ''
})

async function move(lotId: string, delta: number, reason: string) {
    const { error } = await client.rpc('apply_stock_movement', {
        p_lot_id: lotId, p_delta: delta, p_reason: reason, p_note: state.note || null,
    } as never)
    if (error) throw error
}

async function onSubmit() {
    const qty = Number(state.qty)

    saving.value = true
    try {
        if (props.mode === 'restock') {
            let lotId = props.lot?.id
            if (!lotId) {
                const { data, error } = await client.rpc('upsert_stock_lot', {
                    p_post_id: props.post.id,
                    p_storage_id: state.storage_id,
                    p_item_id: state.item_id,
                    p_lot_number: state.lot_number || null,
                    p_expires_on: state.expires_on || null,
                } as never)
                if (error) throw error
                lotId = data as unknown as string
            }
            await move(lotId, qty, 'restock')
        } else if (props.mode === 'adjust') {
            const delta = Number((qty - Number(props.lot!.qty_on_hand)).toFixed(3))
            if (delta === 0) {
                toast.add({ title: 'No change recorded.', color: 'gray', icon: 'i-ph-info-duotone' })
                dismiss()
                return
            }
            await move(props.lot!.id, delta, 'adjust')
        } else if (props.mode === 'transfer') {
            const { error } = await client.rpc('transfer_stock', {
                p_lot_id: props.lot!.id, p_qty: qty, p_dest_storage_id: state.dest_storage_id, p_note: state.note || null,
            } as never)
            if (error) throw error
        } else {
            await move(props.lot!.id, -qty, props.mode)
        }

        toast.add({ title: 'Stock updated.', color: 'green', icon: 'i-ph-check-circle-duotone' })
        emit('reload')
        dismiss()
    } catch (e: any) {
        toast.add({
            title: 'Could not update stock.',
            description: e?.message,
            color: 'red',
            icon: 'i-ph-x-circle-duotone',
        })
    } finally {
        saving.value = false
    }
}

function dismiss() {
    emit('update:open', false)
}
</script>
